import { Button, Group, Loader, Menu, Table, Text } from '@mantine/core';
import { IconDots, IconDownload, IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import dataCaptureClasses from 'src/data-capture/DataCapture.module.css';
import { useDeleteSuitabilityReport } from 'src/suitability-report/hooks/useDeleteSuitabilityReport';
import { useSuitabilityReports } from 'src/suitability-report/hooks/useSuitabilityReports';
import { useSuitability } from 'src/suitability-report/SuitabilityProvider';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import classes from './files-table/FilesTable.module.css';
import SuitabilityReportStatusPill from './SuitabilityReportStatusPill';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import FileUploadLayout from './FileUploadLayout';
import EmptyView from './components/EmptyView';
import FileNameWithIcon from './files-table/FileNameWithIcon';
import { dateMatchesSearch, formatDateTime } from 'src/meetings/utils/date-utils';

export default function SuitabilityReports() {
  const { suitabilityReports, isLoading, isError, error, refetch } = useSuitabilityReports();
  const { clientId } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const { deleteSuitabilityReport } = useDeleteSuitabilityReport();
  const { open: openSuitabilityFlow } = useSuitability();

  const [searchValue, setSearchValue] = useState('');

  const handleDownload = async (reportFileId: number) => {
    try {
      const link = await createLink(reportFileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download suitability report',
        'An error occurred while trying to download the suitability report. Please try again later.',
        e,
        { clientId, reportFileId },
      );
      return;
    }
  };

  const handleDelete = async (reportId: string) => {
    try {
      await deleteSuitabilityReport(clientId, reportId);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete suitability report',
        'An error occurred while trying to delete the suitability report. Please try again later.',
        e,
        { clientId, reportId },
      );
      return;
    }

    await refetch();
  };

  const filteredSuitabilityReports = useMemo(() => {
    if (!searchValue.trim()) return suitabilityReports;

    const searchLower = searchValue.toLowerCase();
    return suitabilityReports.filter(
      report =>
        (report.displayName || '').toLowerCase().includes(searchLower) ||
        dateMatchesSearch(report.createdAt, searchLower),
    );
  }, [suitabilityReports, searchValue]);

  const rows = useMemo(
    () =>
      filteredSuitabilityReports.map(report => (
        <Table.Tr key={report.id}>
          <Table.Td className={classes.fileNameCell}>
            <FileNameWithIcon
              fileName={report.displayName}
              isFolder={false}
              isGenerated={report.isGenerated}
            />
          </Table.Td>
          <Table.Td>
            <Text size="sm">{formatDateTime(report.createdAt)}</Text>
          </Table.Td>
          <Table.Td>
            <SuitabilityReportStatusPill status={report.backgroundProcess?.status ?? 'SUCCESS'} />
          </Table.Td>
          <Table.Td align="right">
            <Group justify="space-between" wrap="nowrap">
              {report.reportFileId ? (
                <Button
                  variant="primary"
                  onClick={() => handleDownload(report.reportFileId!)}
                  rightSection={<IconDownload size={16} />}
                >
                  Download
                </Button>
              ) : (
                <div />
              )}
              <Menu
                transitionProps={{ transition: 'pop-top-right' }}
                position="bottom-end"
                width={220}
                withinPortal
              >
                <Menu.Target>
                  <IconDots color="#475467" stroke={2} size={20} />
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => handleDelete(report.id)}>Delete</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Table.Td>
        </Table.Tr>
      )),
    [filteredSuitabilityReports],
  );

  if (isError || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (filteredSuitabilityReports.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title="Suitability reports">
        <EmptyView
          title="Generate comprehensive suitability reports"
          subtitle="Create detailed suitability reports using Siesta AI to ensure compliance and provide clear investment recommendations."
          ctaText="Generate new"
          ctaIcon={IconPlus}
          onCtaClick={openSuitabilityFlow}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title="Suitability reports"
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <Button
          variant="primary"
          leftSection={<IconPlus size={16} />}
          size="md"
          onClick={openSuitabilityFlow}
        >
          Generate
        </Button>
      }
    >
      <Table
        variant="bare"
        captionSide="bottom"
        classNames={{ th: dataCaptureClasses.th }}
        verticalSpacing={'sm'}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Report Type</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {isLoading ? (
            <Table.Tr>
              <Table.Td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader size="sm" />
              </Table.Td>
            </Table.Tr>
          ) : (
            rows
          )}
        </Table.Tbody>
      </Table>
    </FileUploadLayout>
  );
}
